import * as React from 'react';
import { parseApiError } from '@ooh/contracts';
import type { AddOfferUnitsResponse } from '@ooh/contracts';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import { useAddUnitsToOffer, useCreateOffer } from '../../offers/api/mutations';
import { markOfferAsLastUsedDraft, useSetLastUsedOfferDraftMutation } from './api/user-preferences';
import { buildCreateOfferPayload } from './lib/build-create-offer-payload';
import { mapOfferCreationForbiddenError } from './lib/map-offer-creation-forbidden-error';
import { showQuickAddToast, warnOnAddConflicts } from './quick-add-feedback';
import type {
  QuickAddOfferPickerConfirmArgs,
  QuickAddOfferPickerConfirmResult,
} from './quick-add-offer-picker';

export type { QuickAddOfferPickerConfirmArgs as QuickAddPipelineConfirmArgs };

type AddUnits = (offerId: number, mediaUnitIds: number[]) => Promise<AddOfferUnitsResponse>;

function buildQuickAddFailureContext(args: QuickAddOfferPickerConfirmArgs) {
  // `clientId` substitutes for the not-yet-existing offer id in 'creating'
  // mode, so concurrent failures for different clients read as distinct
  // errors instead of the identical literal "new draft" (PR #1128 review).
  const targetDescription =
    args.mode === 'creating'
      ? `new draft for client ${args.clientId ?? 'unknown'}`
      : `offer ${args.offerId ?? 'unknown'}`;

  return `Quick-add failed during ${args.mode} flow for ${targetDescription} with ${args.mediaUnitIds.length} media units`;
}

function rethrowQuickAddFailure(args: QuickAddOfferPickerConfirmArgs, error: unknown): never {
  throw new Error(buildQuickAddFailureContext(args), { cause: error });
}

function isMissingTargetOfferIdError(error: unknown): error is Error {
  return error instanceof Error && error.message.startsWith('Quick-add cannot continue');
}

function handleQuickAddError(
  args: QuickAddOfferPickerConfirmArgs,
  error: unknown,
  t: TFunction,
): QuickAddOfferPickerConfirmResult {
  if (isMissingTargetOfferIdError(error)) {
    rethrowQuickAddFailure(args, error);
  }

  const parsed = parseApiError(error);
  if (parsed.statusCode === 403) {
    toast.error(t('quickAdd.errors.forbidden'));
    return { status: 'recover-forbidden' };
  }

  rethrowQuickAddFailure(args, error);
}

/**
 * Distinct from `handleQuickAddError`: this covers a failure while
 * *creating* the offer itself (`mode: 'creating'`), before any offer exists.
 * A 403 here (e.g. a sales-department caller lacking
 * `offer.assign_other_salesperson`) is not a "lost edit access to an
 * existing offer" case — `handleQuickAddError`'s generic "Brak uprawnień do
 * tej oferty roboczej" toast and its reset-to-draft-list recovery step are
 * both wrong here, since there is no draft offer to reference or recover to
 * (offer-client-collaboration, CU-86cbc2qv8).
 */
function handleQuickAddCreationError(
  args: QuickAddOfferPickerConfirmArgs,
  error: unknown,
  t: TFunction,
): QuickAddOfferPickerConfirmResult {
  const result = mapOfferCreationForbiddenError(error, t);
  if (result) return result;

  rethrowQuickAddFailure(args, error);
}

/**
 * Resolves the confirm target when a new offer must be created first
 * (`mode: 'creating'`). Kept separate from the "add units" step below so a
 * creation failure (e.g. a 403 from `offer.assign_other_salesperson`) can be
 * mapped by `handleQuickAddCreationError` instead of `handleQuickAddError`.
 */
async function createQuickAddOfferTarget(
  args: QuickAddOfferPickerConfirmArgs,
  createOffer: ReturnType<typeof useCreateOffer>,
): Promise<{ offerId: string; offerName: string }> {
  const createdOffer = await createOffer.mutateAsync(buildCreateOfferPayload(args));
  if (createdOffer.id == null) {
    throw new Error(
      `Quick-add cannot continue during ${args.mode} flow: missing target offer id for ${args.mediaUnitIds.length} media units`,
    );
  }
  return { offerId: String(createdOffer.id), offerName: createdOffer.title };
}

/** Resolves the confirm target for an already-existing offer (`mode !== 'creating'`). */
function resolveExistingQuickAddTarget(args: QuickAddOfferPickerConfirmArgs): {
  offerId: string;
  offerName: string;
} {
  if (args.offerId == null) {
    throw new Error(
      `Quick-add cannot continue during ${args.mode} flow: missing target offer id for ${args.mediaUnitIds.length} media units`,
    );
  }
  return { offerId: args.offerId, offerName: args.offerName ?? args.offerId };
}

type LastUsedDraftMutation = ReturnType<typeof useSetLastUsedOfferDraftMutation>;

/**
 * Adds units to a freshly created `creating`-mode draft. The offer already
 * exists by this point, so any failure here gets its own try/catch instead
 * of the generic `handleQuickAddError` below, which would otherwise either
 * reset to a nonexistent draft list (403) or throw uncaught (any other
 * status) — from here on, a failure must still land the caller in the new
 * (possibly empty) draft instead of silently orphaning it (CU-86cbc2vj1).
 */
async function addUnitsAfterCreate(
  target: { offerId: string; offerName: string },
  args: QuickAddOfferPickerConfirmArgs,
  t: TFunction,
  openOffer: (offerId: string) => void,
  setLastUsedOfferDraft: LastUsedDraftMutation,
  addUnits: AddUnits,
): Promise<void> {
  try {
    const response = await addUnits(
      Number(target.offerId),
      args.mediaUnitIds.map((id) => Number(id)),
    );

    markOfferAsLastUsedDraft(target.offerId, args.isLockedTarget, setLastUsedOfferDraft);

    showQuickAddToast(
      t,
      {
        offerId: target.offerId,
        offerName: target.offerName,
        addedCount: response.addedCount,
        skippedCount: response.skippedCount,
        totalCount: response.units.length,
      },
      openOffer,
      // Already navigating the caller into the offer workspace (see
      // QuickAddOfferPicker's confirm-result handling) — the "Otwórz
      // ofertę" action would be redundant.
      { includeOpenOfferAction: false },
    );
    warnOnAddConflicts(t, response.conflictedMediaUnitIds);
  } catch (error) {
    // Never rethrow here — the offer already exists and the caller must
    // still navigate to it (CU-86cbc2vj1) — but don't swallow the error
    // context-free either. Mirrors create-offer-page.tsx's identical
    // add-after-create failure log (CU-86cb1nqwv) — same message and
    // console.warn severity — so both sites stay distinguishable in
    // monitoring the same way rethrowQuickAddFailure's embedded
    // offer/client id and unit count do elsewhere in this file (PR #1128).
    console.warn('Failed to add quick-add media units to newly created offer', {
      offerId: target.offerId,
      mediaUnitIds: args.mediaUnitIds,
      error,
    });
    toast.error(t('quickAdd.errors.addUnitsFailedAfterCreate'));
  }
}

/** Adds units to an already-existing offer (`mode !== 'creating'`). */
async function addUnitsToExistingTarget(
  args: QuickAddOfferPickerConfirmArgs,
  t: TFunction,
  openOffer: (offerId: string) => void,
  setLastUsedOfferDraft: LastUsedDraftMutation,
  addUnits: AddUnits,
): Promise<QuickAddOfferPickerConfirmResult> {
  try {
    const target = resolveExistingQuickAddTarget(args);
    const response = await addUnits(
      Number(target.offerId),
      args.mediaUnitIds.map((id) => Number(id)),
    );

    markOfferAsLastUsedDraft(target.offerId, args.isLockedTarget, setLastUsedOfferDraft);

    showQuickAddToast(
      t,
      {
        offerId: target.offerId,
        offerName: target.offerName,
        addedCount: response.addedCount,
        skippedCount: response.skippedCount,
        totalCount: response.units.length,
      },
      openOffer,
    );
    warnOnAddConflicts(t, response.conflictedMediaUnitIds);

    return { status: 'success', offerId: target.offerId };
  } catch (error) {
    return handleQuickAddError(args, error, t);
  }
}

/**
 * `openOffer` is the app's navigation to an offer (the toast action); screens never import a router.
 */
export function useQuickAddPipeline({ openOffer }: { openOffer: (offerId: string) => void }) {
  const { t } = useTranslation(['common']);
  const createOffer = useCreateOffer();
  const { mutateAsync: addUnitsToOffer } = useAddUnitsToOffer();
  const setLastUsedOfferDraft = useSetLastUsedOfferDraftMutation();

  const addUnits = React.useCallback<AddUnits>(
    (offerId, mediaUnitIds) => addUnitsToOffer({ offerId, mediaUnitIds }),
    [addUnitsToOffer],
  );

  const confirm = React.useCallback(
    async (args: QuickAddOfferPickerConfirmArgs): Promise<QuickAddOfferPickerConfirmResult> => {
      if (args.mode !== 'creating') {
        return addUnitsToExistingTarget(args, t, openOffer, setLastUsedOfferDraft, addUnits);
      }

      let target: { offerId: string; offerName: string };
      try {
        target = await createQuickAddOfferTarget(args, createOffer);
      } catch (error) {
        // Returns early (bypassing the add-units step below) so a creation
        // failure is never mistaken for the "lost edit access to an
        // existing offer" case handleQuickAddError handles.
        return handleQuickAddCreationError(args, error, t);
      }

      await addUnitsAfterCreate(target, args, t, openOffer, setLastUsedOfferDraft, addUnits);
      return { status: 'success', offerId: target.offerId };
    },
    [addUnits, createOffer, openOffer, setLastUsedOfferDraft, t],
  );

  return { confirm };
}
